import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
// import { useNavigate, useParams } from "react-router-dom";
import { styled } from "@mui/material/styles";
import {
  Box,
  Button,
  Card,
  IconButton,
  Modal,
  Typography,
  linearProgressClasses,
  Menu,
  MenuItem,
  LinearProgress,
  CircularProgress,
  Alert,
} from "@mui/material";

import {
  formatArcScore,
  getScoreAttributes,
  normalizeRowData,
  isUnderwritingActive,
} from "./utils/Helper";
import getTenantIdFromURL from "./utils/getTenantIdFromURL";
import { anyRefreshing, subscribeRefreshing, isTenantRefreshing, clearTenantRefreshing } from "./utils/refreshRegistry";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import bgArc from "./Assets/Images/bg-arc.svg";
import RiskDistributionDiagram from "./RiskDistributionDiagram/RiskDistributionDiagram";
import RecommendationTable from "./RecommendationTable/RecommendationTable";
import ARHeatmapNew from "./ARHeatmapNew";
import AgingImportLog from "./components/AgingImportLog";
import ReceivablesWorkspace from "./components/ReceivablesWorkspace";
import CloseIcon from "@mui/icons-material/Close";
import { useSelector } from "react-redux";
import { useAuth0 } from "@auth0/auth0-react";
import { listCompanySummaryData } from "./api/underwritingApi";
import { useProspectsAR } from "./context/prospectsARContext";
import PortfolioScoreChart from "./PortfolioScoreChart";
import { useSegment } from './context/segmentContext';
import tenantApi from "./api/tenantApi";
import { getInsuranceRequests } from "./api/insuranceApi";
import { buildInsuredLimitMap } from "./utils/insuredLimit";
import useIntegratedDcl from "./features/useIntegratedDcl";
import useBrokerLimitComparisons from "./features/useBrokerLimitComparisons";
import { selectRequesterIdentityReady } from "./app/userInfoSlice";
import IntegratedPolicyAction from "./components/integrated-dcl/IntegratedPolicyAction";
import { toast } from "react-toastify";
import { SERVICE_URLS } from "./api/authApiClient";
import { publish, subscribe, unsubscribe } from "./events";
import { useRecentlyAddedCompanies } from "./context/recentlyAddedCompaniesContext";
import { useStatus } from "./context/statusContext";
import { cancelBulkUpload, getBulkUploadProgress } from "./api/companyApi";
import ProspectUnderwritingProgress from "./components/ProspectUnderwritingProgress";
import QuickTasksTable from "./components/QuickTasksTable";
import ExecutiveOverview from "./components/ExecutiveOverview";
import { isExecutiveDashboardEnabled } from "./config/tenantRollouts";
import { pendingRecentlyAddedCompanies, recentlyAddedCompanyRow } from "./utils/recentlyAddedRows";
import { getProspectAssessmentState, hasAssessmentResult } from "./utils/prospectAssessment";

const CreateColumnHeader = (headerTitle) => {
  return (
    <Typography variant="caption" fontWeight="500">
      {headerTitle}
    </Typography>
  );
};

export default function AllSummaryPage({ adminAction }) {
  console.log("AllSummaryPage component rendering with adminAction:", adminAction);
  
  // State Management
  const [summary, setSummary] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [agingUpdatedUpTo, setAgingUpdatedUpTo] = useState('');
  const [tenantId, setTenantId] = useState();
  const [isAREnabled, setIsAREnabled] = useState(false);
  const [insuranceScope, setInsuranceScope] = useState({ tenantId: null, enabled: false });
  const isInsuranceEnabled = insuranceScope.tenantId === tenantId && insuranceScope.enabled === true;
  const tenantInfoRequest = useRef(0);
  const [isQuickTasksEnabled, setIsQuickTasksEnabled] = useState(false);
  const [quickTasksConfig, setQuickTasksConfig] = useState({ maxRows: 5, defaultFilter: 'new' });
  const [insuredLimitMap, setInsuredLimitMap] = useState(null);
  const [portfolioScore, setPortfolioScore] = useState(0);
  const [popupOpen, setPopupOpen] = useState(false);
  const [showAtRisk, setShowAtRisk] = useState(false);
  const [hasApprovalRules, setHasApprovalRules] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [approvedCount, setApprovedCount] = useState(0);
  const { status, setStatus } = useStatus();
  const [isLoadingTenantInfo, setIsLoadingTenantInfo] = useState(true);
  const [isLoadingSummary, setIsLoadingSummary] = useState(true);
  const [summaryLoadError, setSummaryLoadError] = useState(false);
  const hasLoadedSummaryRef = useRef(false);
  const summaryRequestId = useRef(0);
  const summaryInFlight = useRef(new Map());
  const prospectProgressRequestId = useRef(0);
  const rowIdentities = useRef({ tenantId: null, ids: new Map() });
  const activeSummaryTenant = useRef(null);
  const [loadedSummaryTenant, setLoadedSummaryTenant] = useState(null);
  const [prospectUploadProgress, setProspectUploadProgress] = useState(null);
  const [dismissedProspectJobId, setDismissedProspectJobId] = useState(null);
  const [isCancellingProspectUpload, setIsCancellingProspectUpload] = useState(false);

  const [rows, setRows] = useState([]);
  const [processedData, setProcessedData] = useState([]);
  const [prospectsRows, setProspectsRows] = useState([]);
  const isAdmin = useSelector((state) => state.userInfo.isAdmin);
  const isDistributor = useSelector((state) => state.userInfo.isDistributor);
  const currentTenant = useSelector((state) => state.userInfo.tenantId);
  const URLTenant = getTenantIdFromURL();
  const selectedTenantId = isAdmin || (isDistributor && URLTenant) ? URLTenant : currentTenant;
  const stableRowId = useCallback(companyId => {
    if (rowIdentities.current.tenantId !== selectedTenantId) {
      rowIdentities.current = { tenantId: selectedTenantId, ids: new Map() };
    }
    const ids = rowIdentities.current.ids;
    if (!ids.has(companyId)) ids.set(companyId, ids.size + 1);
    return ids.get(companyId);
  }, [selectedTenantId]);
  const { user: authenticatedUser } = useAuth0();
  const requesterId = authenticatedUser?.sub;
  const identityReady = useSelector(state => selectRequesterIdentityReady(state, requesterId));
  const insuranceScopeReady = identityReady && tenantId === selectedTenantId;
  const directInsuranceAccess = insuranceScopeReady && !isDistributor && (isAdmin || currentTenant === selectedTenantId);
  const insuranceCompanyIds = insuranceScopeReady && loadedSummaryTenant === selectedTenantId ? [...rows, ...prospectsRows].map(row => row.companyId) : [];
  const integratedDcl = useIntegratedDcl({ tenantId: selectedTenantId, insuranceEnabled: isInsuranceEnabled && directInsuranceAccess, isDistributor, requesterId, identityReady, companyIds: insuranceCompanyIds, revision: summary });
  const brokerLimitComparisons = useBrokerLimitComparisons({ tenantId: selectedTenantId, insuranceEnabled: isInsuranceEnabled && insuranceScopeReady, isDistributor, requesterId, identityReady, companyIds: insuranceCompanyIds, revision: summary });

  const prospectProgressView = useMemo(() => {
    if (!prospectUploadProgress) return null;
    const companyIds = Array.isArray(prospectUploadProgress.companyIds) ? prospectUploadProgress.companyIds : [];
    const companyIdSet = new Set(companyIds);
    const jobStartedAt = Date.parse(prospectUploadProgress.createdAt) || 0;
    const timestampMs = (value) => {
      if (!value) return 0;
      if (typeof value?.seconds === 'number') return value.seconds * 1000;
      if (typeof value?._seconds === 'number') return value._seconds * 1000;
      const parsed = Date.parse(value);
      return Number.isFinite(parsed) ? parsed : 0;
    };
    let underwritingCompleted = 0;
    let underwritingInProgress = 0;
    let lastUnderwritingAt = null;
    (summary || []).forEach((row) => {
      if (!companyIdSet.has(row.companyId)) return;
      const completedAt = timestampMs(row.lastUnderwritingDate);
      if (completedAt >= jobStartedAt && row.underwritingInProgress !== true) {
        underwritingCompleted += 1;
        if (!lastUnderwritingAt || completedAt > Date.parse(lastUnderwritingAt)) {
          lastUnderwritingAt = new Date(completedAt).toISOString();
        }
      } else if (row.underwritingInProgress === true) {
        underwritingInProgress += 1;
      }
    });
    const totalCompanies = Number(prospectUploadProgress.totalCompanies || 0);
    const processedCompanies = Number(prospectUploadProgress.processedCompanies || 0);
    const underwritingTotal = companyIds.length;
    const underwritingQueued = Math.max(0, underwritingTotal - underwritingCompleted - underwritingInProgress);
    const completed = processedCompanies >= totalCompanies && underwritingTotal > 0 && underwritingCompleted >= underwritingTotal;
    const status = ['failed', 'cancelled'].includes(prospectUploadProgress.status)
      ? prospectUploadProgress.status
      : processedCompanies < totalCompanies
        ? 'processing_companies'
        : completed ? 'completed' : 'underwriting';
    const workTotal = Math.max(1, totalCompanies + underwritingTotal);
    const overallPercent = completed ? 100 : Math.min(99, Math.round((Math.min(processedCompanies, totalCompanies) + underwritingCompleted) / workTotal * 100));
    return {
      ...prospectUploadProgress,
      status,
      underwritingTotal,
      underwritingCompleted,
      underwritingInProgress,
      underwritingQueued,
      lastUnderwritingAt,
      overallPercent,
    };
  }, [prospectUploadProgress, summary]);

  // The underwriting service creates its summary document asynchronously. Add the company
  // records tracked by the current import job so Priority actions does not wait for that document
  // before rendering an accepted upload row.
  const quickTaskCompanies = useMemo(() => {
    const knownCompanyIds = new Set((companies || []).map((company) => company.companyId));
    const queuedCompanies = (prospectUploadProgress?.companies || [])
      .filter((company) => company?.companyId && !knownCompanyIds.has(company.companyId))
      .map((company) => ({
        companyId: company.companyId,
        'Company Name': company.companyName,
        createdAt: company.createdAt,
        requestedCreditLimit: company.requestedCreditLimit,
        hasSummaryData: false,
        underwritingInProgress: false,
      }));
    return [...(companies || []), ...queuedCompanies];
  }, [companies, prospectUploadProgress]);
  
  // Context and Router Hooks
  // const { tenantIdp } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const bottomRef = useRef(null);

  activeSummaryTenant.current = selectedTenantId;
  const { isProspects } = useProspectsAR();
  const { selectedSegment } = useSegment();
  const { recentlyAddedCompanies, removeRecentlyAddedCompanies } = useRecentlyAddedCompanies();

  // Initialize tenant ID — admins AND distributors viewing a client tenant use the URL tenant.
  useEffect(() => {
    if (isAdmin || (isDistributor && URLTenant)) {
      setTenantId(URLTenant);
    } else {
      setTenantId(currentTenant);
    }
  }, [isAdmin, isDistributor, URLTenant, currentTenant]);

  useEffect(() => {
    if (!tenantId) return;
    hasLoadedSummaryRef.current = false;
    summaryRequestId.current += 1;
    setLoadedSummaryTenant(null);
    setSummary([]);
    setCompanies([]);
    setRows([]);
    setProspectsRows([]);
    setIsLoadingSummary(true);
    setSummaryLoadError(false);
    setProspectUploadProgress(null);
    setIsCancellingProspectUpload(false);
    prospectProgressRequestId.current += 1;
    try {
      setDismissedProspectJobId(localStorage.getItem(`prospect-upload-progress-dismissed:${tenantId}`));
    } catch (error) {
      setDismissedProspectJobId(null);
    }
    return () => { summaryRequestId.current += 1; prospectProgressRequestId.current += 1; };
  }, [tenantId]);

  const loadSummaryData = useCallback(async ({ skipIfLoading = false } = {}) => {
    if (!tenantId || tenantId !== activeSummaryTenant.current) return;
    if (skipIfLoading && summaryInFlight.current.has(tenantId)) return;
    const requestId = ++summaryRequestId.current;
    summaryInFlight.current.set(tenantId, requestId);
    const isCurrent = () => requestId === summaryRequestId.current && tenantId === activeSummaryTenant.current;

    try {
      const result = await listCompanySummaryData(tenantId, { view: 'dashboard', includeStatuses: 'Pending,Failed' });
      if (!isCurrent()) return;
      const finalSummaries = result.data.map((summary) => {
        // Handle case where summaryData is already an object
        const parsedData = typeof summary.summaryData === 'string' 
          ? JSON.parse(summary.summaryData)
          : summary.summaryData;
        
        return {
          ...parsedData,
          'Company Name': parsedData?.['Company Name'] || summary.companyName,
          hasSummaryData: parsedData !== undefined && parsedData !== null,
          companyId: summary.companyId,
          tenantId,
          country: summary.country,
          createdAt: summary.createdAt,
          requestedCreditLimit: summary.requestedCreditLimit,
          approvedLimit: summary.approvedLimit,
          approvalStatus: summary.approvalStatus, // Assuming this field exists
          underwritingInProgress: summary.underwritingInProgress, // drives the in-row "underwriting…" spinner
          underwritingStartedAt: summary.underwritingStartedAt, // used to ignore stale/crashed runs
          underwritingStatus: summary.underwritingStatus,
          underwritingDispatchStatus: summary.underwritingDispatchStatus,
          lastUnderwritingDate: summary.underwritingTiming?.at || (summary.receivables || !hasAssessmentResult(parsedData || {}) ? null : summary.updatedAt) || null,
          isArCustomer: summary.isArCustomer, // aging-uploaded customer → always belongs in Account Receivable
          ...(summary.receivables ? { receivables: summary.receivables, receivablesAssessment: summary.receivablesAssessment || summary.receivables.assessment } : {}),
        };
      });
      
      const normalisedData = normalizeRowData(finalSummaries);
      setSummary(normalisedData);
      setCompanies(normalisedData);
      // Retire only receipts actually acknowledged by this tenant's read. A
      // completion event for one company must not discard other accepted adds.
      removeRecentlyAddedCompanies?.(tenantId, normalisedData.map(row => row.companyId));
      hasLoadedSummaryRef.current = true;
      setLoadedSummaryTenant(tenantId);
      setSummaryLoadError(false);
    } catch (error) {
      if (!isCurrent()) return;
      console.error("Error loading summary:", error);
      setLoadedSummaryTenant(tenantId);
      if (!hasLoadedSummaryRef.current) setSummaryLoadError(true);
    } finally {
      if (summaryInFlight.current.get(tenantId) === requestId) summaryInFlight.current.delete(tenantId);
      if (isCurrent()) setIsLoadingSummary(false);
    }
  }, [tenantId, removeRecentlyAddedCompanies]);

  const loadProspectUploadProgress = useCallback(async () => {
    if (!tenantId || tenantId !== activeSummaryTenant.current) return;
    const requestId = ++prospectProgressRequestId.current;
    try {
      const progress = await getBulkUploadProgress(tenantId);
      if (tenantId === activeSummaryTenant.current && requestId === prospectProgressRequestId.current) setProspectUploadProgress(progress);
    } catch (error) {
      console.error('Error loading prospect upload progress:', error);
    }
  }, [tenantId]);

  const dismissProspectUploadProgress = useCallback(() => {
    const jobId = prospectProgressView?.id;
    if (!jobId) return;
    setDismissedProspectJobId(jobId);
    try {
      localStorage.setItem(`prospect-upload-progress-dismissed:${tenantId}`, jobId);
    } catch (error) {
      // Dismissing still works for this page session when storage is unavailable.
    }
  }, [prospectProgressView?.id, tenantId]);

  const stopProspectUpload = useCallback(async () => {
    const jobId = prospectProgressView?.id;
    if (!tenantId || tenantId !== activeSummaryTenant.current || !jobId || isCancellingProspectUpload) return;
    const confirmed = window.confirm('Stop this upload? Companies already processed and underwriting already started will not be rolled back.');
    if (!confirmed) return;

    setIsCancellingProspectUpload(true);
    try {
      const stoppedProgress = await cancelBulkUpload(tenantId, jobId);
      if (tenantId !== activeSummaryTenant.current) return;
      if (stoppedProgress) setProspectUploadProgress(stoppedProgress);
      toast.info('Prospect upload stopped.');
    } catch (error) {
      if (tenantId !== activeSummaryTenant.current) return;
      console.error('Error stopping prospect upload:', error);
      toast.error(error?.response?.data?.message || 'Could not stop the prospect upload.');
    } finally {
      if (tenantId === activeSummaryTenant.current) setIsCancellingProspectUpload(false);
    }
  }, [isCancellingProspectUpload, prospectProgressView?.id, tenantId]);

  useEffect(() => { loadProspectUploadProgress(); }, [loadProspectUploadProgress]);

  useEffect(() => {
    const refreshProgress = () => loadProspectUploadProgress();
    subscribe('prospect_import_started', refreshProgress);
    return () => unsubscribe('prospect_import_started', refreshProgress);
  }, [loadProspectUploadProgress]);

  useEffect(() => {
    if (!prospectProgressView || ['completed', 'failed', 'cancelled'].includes(prospectProgressView.status)) return;
    const timer = setInterval(() => {
      loadProspectUploadProgress();
      loadSummaryData();
    }, 5000);
    return () => clearInterval(timer);
  }, [prospectProgressView, loadProspectUploadProgress, loadSummaryData]);

  // WebSocket connection
  useEffect(() => {
    if (!tenantId) return;

    let ws;
    let reconnectInterval;

    const connect = () => {
        console.log('[WS] Calling connect...');
        const wsUrl = `${SERVICE_URLS?.webSocketServer}?tenantId=${tenantId}`;
        ws = new WebSocket(wsUrl);

        ws.onopen = () => {
            console.log(`[WS] Connection opened to ${wsUrl}`);
            if (reconnectInterval) {
                clearInterval(reconnectInterval);
                reconnectInterval = null;
            }
        };

        ws.onmessage = async (event) => {
          console.log("[DEBUG] [WS] WebSocket message received:", JSON.parse(event.data));
            try {
                const message = JSON.parse(event.data);
                if (message.type === 'UW_COMPLETE' || message.type === 'STATUS_UPDATE') { // Handle status updates
                    console.log(`[WS] Received ${message.type} for ${message.data.companyName}, refetching summary...`);
                    await loadSummaryData();
                    if (tenantId !== activeSummaryTenant.current) return;
                    publish('refetch_notifications');
                    if (message.data.alertMessage) {
                      toast.success(`${message.data.alertMessage}`);
                    }
                }
            } catch (error) {
                console.error('[WS] Error processing message:', error);
            }
        };

        ws.onerror = (error) => {
            console.error('[WS] WebSocket error:', error);
            ws.close();
        };

        ws.onclose = (event) => {
            // Log close event details for debugging
            console.log(`[WS] Connection closed. Code: ${event.code}, Reason: ${event.reason}, Was Clean: ${event.wasClean}`);

            // Only attempt to reconnect if the close was unexpected
            if (!event.wasClean) {
                console.log('[WS] Unexpected close. Attempting to reconnect...');
                if (!reconnectInterval) {
                    reconnectInterval = setInterval(() => {
                        console.log('[WS] Attempting reconnection...');
                        connect();
                    }, 5000);
                }
            }
        };
    };

    connect();

    return () => {
        console.log('[WS] Cleanup: Closing WebSocket connection.');
        if (reconnectInterval) {
            clearInterval(reconnectInterval);
        }
        if (ws) {
            // Remove the onclose handler before closing to prevent reconnection attempts on unmount
            ws.onclose = null;
            ws.close();
        }
    };
}, [tenantId, loadSummaryData]);

  // Load tenant info
  const fetchTenantInfo = useCallback(async () => {
    const requestId = ++tenantInfoRequest.current;
    setInsuranceScope({ tenantId, enabled: false });
    setIsLoadingTenantInfo(true);
    try {
      const tenantInfo = await tenantApi.getTenantById(tenantId);
      if (requestId !== tenantInfoRequest.current) return;
      console.log("tenantInfo",tenantInfo)
      setAgingUpdatedUpTo(tenantInfo?.data?.agingUpdatedUpTo);
      setHasApprovalRules(tenantInfo?.data?.hasApprovalRules || false);
      setIsAREnabled(tenantInfo?.data?.isAREnabled || false);
      setInsuranceScope({ tenantId, enabled: tenantInfo?.data?.isInsuranceEnabled === true });
      setIsQuickTasksEnabled(tenantInfo?.data?.isQuickTasksEnabled === true);
      setQuickTasksConfig({ maxRows: 5, defaultFilter: 'new', ...(tenantInfo?.data?.quickTasksConfig || {}) });
    } catch (error) {
      console.error("Error fetching tenant info:", error);
    } finally {
      if (requestId === tenantInfoRequest.current) setIsLoadingTenantInfo(false);
    }
  }, [tenantId]);

  // Calculate portfolio score when summary changes
  useEffect(() => {
    if (processedData.length > 0) {
      const validScores = processedData
        .map((item) => item["Combined Score"])
        .filter((score) => !isNaN(score) && score !== null && score !== "");

      if (validScores.length > 0) {
        const totalCombinedScore = validScores.reduce((sum, score) => sum + score, 0);
        const averageCombinedScore = totalCombinedScore / validScores.length;
        setPortfolioScore(averageCombinedScore.toFixed(1));
      } else {
        setPortfolioScore(0);
      }
    } else {
      setPortfolioScore(0);
    }
  }, [processedData]);

  // Load data when tenant ID changes
  useEffect(() => {
    if (!tenantId) return;
    fetchTenantInfo();
    loadSummaryData();
  }, [tenantId, fetchTenantInfo, loadSummaryData]);

  // Re-evaluate the polling effect whenever a per-row refresh is triggered from
  // the table (the shared refresh registry), so polling starts immediately even
  // for a re-run of a company that wasn't previously underwriting.
  const [refreshTick, setRefreshTick] = useState(0);
  const bulkRefreshSeenActive = useRef(false);
  const bulkRefreshIdlePolls = useRef(0);
  useEffect(() => subscribeRefreshing(() => setRefreshTick((t) => t + 1)), []);

  // While any company is underwriting (just added, being re-run, or a row refresh
  // was just triggered), poll the summaries so the in-row "Underwriting…" spinner
  // and the row's refresh button reflect the run live and then clear — no manual reload.
  useEffect(() => {
    const anyInProgress =
      recentlyAddedCompanies?.some(company => company.tenantId === tenantId) ||
      anyRefreshing() ||
      isTenantRefreshing(tenantId) ||
      (Array.isArray(processedData) && processedData.some((r) => isUnderwritingActive(r) || ['assessing', 'delayed', 'unconfirmed'].includes(getProspectAssessmentState(r))));
    if (!anyInProgress) return;
    const iv = setInterval(() => { loadSummaryData({ skipIfLoading: true }); }, 5000);
    return () => clearInterval(iv);
  }, [recentlyAddedCompanies, processedData, loadSummaryData, refreshTick, tenantId]);

  // Refresh immediately when another tab broadcasts that a tenant-wide run started.
  useEffect(() => {
    if (tenantId && isTenantRefreshing(tenantId)) loadSummaryData();
  }, [refreshTick, tenantId, loadSummaryData]);

  // A bulk refresh can be started from another tab. Once this dashboard has observed the
  // backend flags turn on and then off, clear the shared tenant marker and stop the extra polling.
  // Three empty polls also clear a request that never reached underwriting.
  useEffect(() => {
    if (!tenantId || !isTenantRefreshing(tenantId) || !Array.isArray(processedData)) return;
    const hasActiveRows = processedData.some((row) => isUnderwritingActive(row));
    if (hasActiveRows) {
      bulkRefreshSeenActive.current = true;
      bulkRefreshIdlePolls.current = 0;
      return;
    }
    bulkRefreshIdlePolls.current += 1;
    if (bulkRefreshSeenActive.current || bulkRefreshIdlePolls.current >= 3) {
      clearTenantRefreshing(tenantId);
      bulkRefreshSeenActive.current = false;
      bulkRefreshIdlePolls.current = 0;
    }
  }, [processedData, tenantId]);

  // The AR import modal fires this on close so the table reflects the just-replaced/imported AR
  // immediately, instead of showing the stale previous table until the next 15s auto-refresh.
  useEffect(() => {
    const onRefresh = () => { loadSummaryData(); };
    subscribe("ar_dashboard_refresh", onRefresh);
    return () => unsubscribe("ar_dashboard_refresh", onRefresh);
  }, [loadSummaryData]);

  // Process summary data with company creation dates and segment filtering
  useEffect(() => {
    if (summary && companies) {
      // Create company creation date mapping
      const companyCreatedAtMap = companies.reduce((acc, company, index) => {
        let processedCreatedAt = null;
        
        if (company.createdAt) {
          if (typeof company.createdAt === 'object' && company.createdAt.seconds) {
            // Firestore timestamp object with 'seconds' property
            processedCreatedAt = new Date(company.createdAt.seconds * 1000);
          } else if (typeof company.createdAt === 'object' && company.createdAt._seconds) {
            // Firestore timestamp object with '_seconds' property
            processedCreatedAt = new Date(company.createdAt._seconds * 1000);
          } else if (typeof company.createdAt === 'string') {
            // Regular date string
            processedCreatedAt = new Date(company.createdAt);
          } else if (company.createdAt instanceof Date) {
            // Already a Date object
            processedCreatedAt = company.createdAt;
          }
        }
        
        // Check if key already exists
        if (acc[company.companyName]) {
          console.warn(`Duplicate company name found: "${company.companyName}"`);
        }
        
        const key = company.companyId;
        acc[key] = processedCreatedAt;
        return acc;
      }, {});

      // Process summary data with IDs and creation dates
      const dataWithIds = summary.map((obj, index) => {
        const { id, ...rest } = obj;
        const createdAt = companyCreatedAtMap[obj.companyId];

        return {
          id: stableRowId(obj.companyId),
          ...rest,
          createdAt: createdAt,
          createdAtFormatted: createdAt ? createdAt.toLocaleDateString() : null
        };
      });

      // Sort by ID in descending order
      // Sort using createdAtFormatted id descending order
      dataWithIds.sort((a, b) => {
        if (a.createdAt && b.createdAt) {
          return new Date(b.createdAt) - new Date(a.createdAt);
        }
        return 0;
      });

      // Add recently added companies to the top of the list
      const pendingCompanies = pendingRecentlyAddedCompanies(
        recentlyAddedCompanies,
        summary,
        selectedTenantId
      );
      const newCompanyRows = pendingCompanies.map((company, index) => ({
        id: stableRowId(company.companyId),
        ...recentlyAddedCompanyRow(company),
      }));
      const allData = [...[...newCompanyRows].reverse(), ...dataWithIds];

      // Filter by selected segment if one is selected
      let segmentFilteredData = selectedSegment ? selectedSegment.companies?.items?.length > 0
        ? allData.filter(row => {
            const companyName = row["Company Name"]?.trim();
            if (!companyName) return false;
            const isInSegment = selectedSegment.companies.items.some(
              segmentCompany => {
                const memberId = segmentCompany.id || segmentCompany.companyId;
                if (memberId) return memberId === row.companyId;
                const segmentCompanyName = segmentCompany.name?.trim();
                if (!segmentCompanyName) return false;
                return segmentCompanyName === companyName;
              }
            );
            return isInSegment;
          })
        : [] : allData;

          if (selectedSegment) {
            segmentFilteredData = [...new Map([
              ...newCompanyRows.filter(
                (rc) => rc?.segmentID === selectedSegment?.id
              ),
              ...segmentFilteredData,
            ].map(row => [row.companyId, row])).values()];
          }

      const pending = segmentFilteredData.filter(row => row.approvalStatus === 'PENDING').length;
      const approved = segmentFilteredData.filter(row => row.approvalStatus === 'APPROVED').length;
      setPendingCount(pending);
      setApprovedCount(approved);

      // Filter by status if a status is selected
      const statusFilteredData = status && status !== 'DASHBOARD'
        ? segmentFilteredData.filter(row => row.approvalStatus === status)
        : segmentFilteredData;

      // Set the processed data
      setProcessedData(statusFilteredData);

      // A row belongs to Account Receivable if it's an aging-uploaded customer (isArCustomer) OR it
      // carries a non-zero AR balance. Aging customers stay in AR even at a $0/credit balance — they
      // are the tenant's actual receivables customers, not prospects. Everything else is a prospect.
      const hasArBalance = (row) =>
        Object.keys(row).some(
          (key) =>
            key.includes("AR Balance") &&
            row[key] !== null &&
            row[key] !== 0 &&
            row[key] !== "" &&
            row[key] !== '0'
        );
      const isArRow = (row) => row.isArCustomer === true || hasArBalance(row);

      const summaryRows = statusFilteredData.filter(isArRow);
      setRows(summaryRows);

      const newProspectsRows = statusFilteredData.filter((row) => !isArRow(row));
      setProspectsRows(newProspectsRows);
    }
  }, [summary, companies, selectedSegment, recentlyAddedCompanies, status, selectedTenantId, stableRowId]);

  // When the tenant has the insurance layer, load its insurance requests and build a
  // companyId -> insured-limit map for the dashboard tables' "Insured Limit" column.
  useEffect(() => {
    let cancelled = false;
    if (!tenantId || !isInsuranceEnabled) { setInsuredLimitMap(null); return; }
    (async () => {
      try {
        const res = await getInsuranceRequests(tenantId);
        if (cancelled) return;
        setInsuredLimitMap(buildInsuredLimitMap(res?.data || []));
      } catch (e) {
        if (!cancelled) setInsuredLimitMap(new Map());
      }
    })();
    return () => { cancelled = true; };
  }, [tenantId, isInsuranceEnabled]);

  // Helper Functions
  const formatNumber = (amount) => {
    return amount.toLocaleString("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  };

  const handleStatusClick = (newStatus) => {
    if (newStatus) {
      setStatus(newStatus);
    } else {
      setStatus('DASHBOARD');
    }
  };


  const handleSettingsClick = () => {
    handleClosePopup();
    navigate("/settings/AREnableRequest");
  };

  const handleClosePopup = () => {
    setPopupOpen(false);
  };

  const bgStyle = {
    width: "100%",
    height: "100%",
    background: `transparent url(${bgArc}) 0% 0% no-repeat padding-box`,
    position: "absolute",
    top: 0,
    left: 0,
    zIndex: 0,
  };

  function updateFields(data) {
    const fieldsToUpdate = [
      "Current Limit",
      "Current AR Balance",
      "AR Balance Aging 30",
      "AR Balance Aging 60",
      "AR Balance Aging 90",
      "AR Balance Aging 90+",
      "Requested Limit",
      "Recommended Limit",
      "Total AR Balance",
    ];

    const fieldsToModify = ["Recommended Net", "Current Net"];

    fieldsToUpdate.forEach((field) => {
      if (data[field] === "-" || data[field] === "") {
        data[field] = null;
      }
    });

    fieldsToModify.forEach((field) => {
      if (
        data[field] === "NET 30" ||
        data[field] === "NET 45" ||
        data[field] === "NET 60" ||
        data[field] === "NET 90" ||
        data[field] === "NET 120" ||
        data[field] === "NET 180"
      ) {
        let formattedValue = data[field].toLowerCase();
        formattedValue =
          formattedValue.charAt(0).toUpperCase() + formattedValue.slice(1);
        data[field] = formattedValue;
      }
    });
  }

  rows?.forEach(updateFields);

  const columnDefinition = [];


  const dateTimeSortComparator = (date1, date2) => {
    if (!date1 && !date2) return 0;
    if (!date1) return 1;
    if (!date2) return -1;

    return new Date(date1) - new Date(date2);
  };

  const columns = getUniqueKeys(rows).filter(
    (column) =>
      column !== "" && column !== "id" && column !== "Evaluation Status"
  );

  columns.forEach((column) => {
    const definition = {
      field: column,
      headerName: column,
      width: 120,
      hide: false,
      renderHeader: (params) => {
        return CreateColumnHeader(params?.colDef?.headerName || column);
      },
    };

    if (rows[0] && typeof rows[0][column] === "number") {
      definition.type = "number";
    }
    if (column === "Requested Limit") {
      definition.editable = true;
    }
    if (column === "Current Net") {
      definition.editable = true;
    }
    if (column === "Current Limit") {
      definition.editable = true;
    }
    if (column === "Last Update Time") {
      definition.sortComparator = dateTimeSortComparator;
    }
    columnDefinition.push(definition);
  });

  const ManualEvaluationButton = {
    field: "toggle",
    headerName: "Request Manual Evaluation",
    width: 130,
    renderCell: (params) => {
      let buttonStyle = {
        backgroundColor: "#2196F3",
        color: "#fff",
        borderRadius: "5px",
        padding: "8px 16px",
        cursor: "pointer",
        border: "none",
        outline: "none",
      };

      let buttonText = "Request";
      let disabled = false;

      if (params.row["Evaluation Status"] === "Sent") {
        buttonText = "Sent";
        disabled = true;
        buttonStyle = {
          ...buttonStyle,
          backgroundColor: "#ccc",
          cursor: "default",
        };
      }

      return (
        <button
          style={buttonStyle}
          onClick={() => {}}
          disabled={disabled}
        >
          {buttonText}
        </button>
      );
    },
  };


  columnDefinition.push(ManualEvaluationButton);


  function getUniqueKeys(arrayOfObjects) {
    let uniqueKeys = new Set();
    arrayOfObjects?.forEach((obj) => {
      for (let key in obj) {
        if (obj.hasOwnProperty(key)) {
          uniqueKeys.add(key);
        }
      }
    });
    return Array.from(uniqueKeys);
  }




  const sortProspectRiskyRows = (gridRowsRC) => {
    return gridRowsRC
      .filter(row => formatArcScore(row["Combined Score"]) >= 8)
      .sort((a, b) => {
        if (a["Combined Score"] != null || b["Combined Score"] != null) {
          return (
            (formatArcScore(b["Combined Score"]) || 0) -
            (formatArcScore(a["Combined Score"]) || 0)
          );
        }
        return 0;
      });
  }

  const sortARriskyRows = (gridRowsRC) => {
    return gridRowsRC
      .filter(row => formatArcScore(row["Combined Score"]) >= 8)
      .sort((a, b) => {
        if (formatArcScore(a["Combined Score"]) != null || formatArcScore(b["Combined Score"]) != null) {
          return (
            (formatArcScore(b["Combined Score"]) || 0) -
            (formatArcScore(a["Combined Score"]) || 0)
          );
        }
        return 0;
      })
  };

  const containerStyle = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "16px",
    paddingTop: "20px",
    marginLeft: "0px",
    width: "100%",
  };

  const BorderLinearProgress = styled(LinearProgress)(({ theme }) => ({
    height: 10,
    borderRadius: 5,
    [`&.${linearProgressClasses.colorPrimary}`]: {
      backgroundColor:
        theme.palette.grey[theme.palette.mode === "light" ? 200 : 800],
      boxShadow: "inset 0px 1px 3px grey, 0px 3px 6px white",
    },
    [`& .${linearProgressClasses.bar}`]: {
      borderRadius: 5,
      backgroundColor: getScoreAttributes(portfolioScore).color,
      boxShadow: "inset 0px 3px 6px #00000029",
    },
  }));

  const countRowsAtRisk = () => {
    return rows.filter((row) => formatArcScore(row["Combined Score"]) >= 8).length;
  };
  const calculateMoneyAtRisk = () => {
    return rows
      .filter((row) => formatArcScore(row["Combined Score"]) >= 8)
      .reduce((total, row) => total + parseFloat(row["Total AR Balance"]), 0);
  };

  const customersAtRisk = countRowsAtRisk();
  const moneyExposedToRisk = calculateMoneyAtRisk();
  const existingCustomers = rows.length;
  const hasExactReceivables = !isProspects && rows.some(row => row.receivables);
  const totalAmount = prospectsRows.reduce(
    (sum, row) => sum + ((status === 'APPROVED' ? row?.approvedLimit : row["Recommended Limit"]) || 0),
    0
  );

  const handleLoadAgingFiles = () => {
    if (!isAREnabled) {
      setPopupOpen(true);
      return;
    }
    // setFileModalOpen(true);
  };

  const DropdownMenu = () => {
    const [anchorEl, setAnchorEl] = useState(null);
    const [isExpandDocuments, setExpandDocuments] = useState(false);
    const [isExpandMitigations, setExpandMitigation] = useState(false);

    const handleClick = (event) => {
      setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
      setAnchorEl(null);
    };

    const handleExpandDocuments = () => {
      setExpandDocuments(!isExpandDocuments);
    };

    const handleExpandMitigations = () => {
      setExpandMitigation(!isExpandMitigations);
    };

    return (
      <div style={{ textAlign: "center" }}>
        <Button
          variant="contained"
          onClick={handleClick}
          style={{
            textTransform: "none",
            fontFamily: "Rubik, sans-serif",
            backgroundColor: "#5186EC",
            height: "25px",
            padding: "0px 1px 0px 5px",
            borderRadius: "8px",
          }}
        >
          <Typography sx={{ fontSize: "12px" }}>
            How to lower the risk?
          </Typography>
          <KeyboardArrowDownIcon />
        </Button>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          transformOrigin={{ vertical: "top", horizontal: "center" }}
          getContentAnchorEl={null}
        >
          <Typography
            variant="subtitle1"
            onClick={handleExpandDocuments}
            style={{
              marginLeft: "15px",
              marginTop: "10px",
              marginRight: "10px",
              fontFamily: "Rubik, sans-serif",
              fontWeight: 450,
              cursor: "pointer",
            }}
          >
            Request Customer Documents
          </Typography>
          {isExpandDocuments && (
            <>
              <MenuItem onClick={handleClose} style={{ paddingLeft: "20px" }}>
                Financial statement
              </MenuItem>
              <hr />
              <MenuItem onClick={handleClose} style={{ paddingLeft: "20px" }}>
                Current account statement
              </MenuItem>
              <hr />
              <MenuItem onClick={handleClose} style={{ paddingLeft: "20px" }}>
                Deposit and Saving statement
              </MenuItem>
              <hr />
            </>
          )}
          <Typography
            variant="subtitle1"
            onClick={handleExpandMitigations}
            style={{
              marginLeft: "15px",
              marginTop: "10px",
              marginRight: "10px",
              fontFamily: "Rubik, sans-serif",
              fontWeight: 450,
              cursor: "pointer",
            }}
          >
            Risk Mitigations
          </Typography>
          {isExpandMitigations && (
            <>
              <MenuItem onClick={handleClose} style={{ paddingLeft: "20px" }}>
                Comfort letter
              </MenuItem>
              <hr />
              <MenuItem onClick={handleClose} style={{ paddingLeft: "20px" }}>
                Parent company guarantee
              </MenuItem>
              <hr />
              <MenuItem onClick={handleClose} style={{ paddingLeft: "20px" }}>
                Owners' guarantee
              </MenuItem>
              <hr />
              <MenuItem onClick={handleClose} style={{ paddingLeft: "20px" }}>
                Government guarantee
              </MenuItem>
              <hr />
              <MenuItem onClick={handleClose} style={{ paddingLeft: "20px" }}>
                Insurance
              </MenuItem>
              <hr />
              <MenuItem onClick={handleClose} style={{ paddingLeft: "20px" }}>
                Collateral
              </MenuItem>
            </>
          )}
        </Menu>
      </div>
    );
  };

  const scrollToTable = useCallback(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  // Deep-link: /tenant-dashboard/:id?view=atrisk opens straight to the "at risk" table (the weekly
  // AR summary email's "Open the AR dashboard" button uses this so recipients land on the risky set).
  useEffect(() => {
    if (new URLSearchParams(location.search).get("view") === "atrisk") {
      setShowAtRisk(true);
      const t = setTimeout(() => scrollToTable(), 700);
      return () => clearTimeout(t);
    }
  }, [location.search, scrollToTable]);

  const handleBottomClick = (e) => {
    e.preventDefault();
    setShowAtRisk(true);
    setTimeout(scrollToTable, 50);
  };

  const {
    show_portfolio_ai_score_card,
    show_customer_risk_bar_chart,
    show_portfolio_score_chart,
    show_prospects_card,
    show_summary_table, 
    show_existing_customer_card,
    show_ar_heatmap
  } = {
    show_portfolio_ai_score_card: true,
    show_customer_risk_bar_chart: true,
    show_portfolio_score_chart: true,
    show_prospects_card: true,
    show_summary_table: true,
    show_existing_customer_card: true,
    show_ar_heatmap: true
  };
  const isExecutiveDashboard = isExecutiveDashboardEnabled(tenantId || URLTenant);
  const unacknowledgedProspects = pendingRecentlyAddedCompanies(recentlyAddedCompanies, [], selectedTenantId)
    .map(company => ({ id: stableRowId(company.companyId), ...recentlyAddedCompanyRow(company) }));

  return (
    <>
      {loadedSummaryTenant === selectedTenantId && isProspects &&
        prospectProgressView?.filename &&
        Number(prospectProgressView?.totalCompanies || 0) > 0 &&
        prospectProgressView?.id !== dismissedProspectJobId && (
        <ProspectUnderwritingProgress
          progress={prospectProgressView}
          onDismiss={dismissProspectUploadProgress}
          onCancel={stopProspectUpload}
          cancelling={isCancellingProspectUpload}
        />
      )}
      {isProspects && unacknowledgedProspects.length > 0 &&
        (isLoadingSummary || loadedSummaryTenant !== selectedTenantId || summaryLoadError) && (
        <RecommendationTable
          rows={unacknowledgedProspects}
          riskyRows={[]}
          showAtRisk={false}
          setShowAtRisk={setShowAtRisk}
          hasApprovalRules={hasApprovalRules}
        />
      )}
      {isLoadingSummary || loadedSummaryTenant !== selectedTenantId ? (
        <Card
          variant="outlined"
          sx={{ minHeight: 360, m: 3, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <Box sx={{ textAlign: 'center' }}>
            <CircularProgress size={38} />
            <Typography sx={{ mt: 2, color: '#2F3D63', fontWeight: 500 }}>
              Loading portfolio…
            </Typography>
            <Typography sx={{ mt: 0.5, color: '#64708D', fontSize: 14 }}>
              Preparing company risk data and recommendations.
            </Typography>
          </Box>
        </Card>
      ) : summaryLoadError ? (
        <Card variant="outlined" sx={{ m: 3, p: 3 }}>
          <Alert
            severity="error"
            action={(
              <Button
                color="inherit"
                size="small"
                onClick={() => {
                  setIsLoadingSummary(true);
                  setSummaryLoadError(false);
                  loadSummaryData();
                }}
              >
                Try again
              </Button>
            )}
          >
            Portfolio could not be loaded. Please try again.
          </Alert>
        </Card>
      ) : (
      <>
      {isExecutiveDashboard && !hasExactReceivables && (
        <ExecutiveOverview
          isProspects={isProspects}
          portfolioScore={portfolioScore}
          prospectsCount={prospectsRows.length}
          approvedLimit={totalAmount}
          existingCustomers={existingCustomers}
          customersAtRisk={customersAtRisk}
          moneyAtRisk={moneyExposedToRisk}
          agingUpdatedUpTo={agingUpdatedUpTo}
          onReviewRisk={handleBottomClick}
          onLoadAging={handleLoadAgingFiles}
        />
      )}
      {isQuickTasksEnabled && (
        <QuickTasksTable
          tenantId={tenantId}
          companies={quickTaskCompanies}
          config={quickTasksConfig}
          adminAction={adminAction}
          onConfigChanged={setQuickTasksConfig}
          onCompaniesChanged={loadSummaryData}
        />
      )}
      {!isExecutiveDashboard && (
      <>
      <div style={containerStyle}>
        {show_portfolio_ai_score_card && !hasExactReceivables && (
          <Card
            variant="outlined"
            style={{
              height: "263px",
              display: "flex",
              flex: 1,
              flexDirection: "column",
              justifyContent: "space-between",
              boxShadow: "inset 0px 1px 3px #0000001A, 0px 3px 6px #00000029",
              position: "relative",
              backgroundColor: "#FAFAFC",
            }}
          >
            <div style={bgStyle}></div>
            <div
              style={{
                padding: "5px 20px 0 20px",
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
              }}
            >
              <Typography
                style={{
                  color: "#2F3D63",
                  fontFamily: "Rubik, sans-serif",
                  fontSize: "20px",
                  alignContent: "left",
                  fontWeight: "500",
                  marginRight: "10px",
                  paddingTop: "10px",
                }}
              >
                Portfolio AI Score
              </Typography>
              <div
                style={{
                  display: "flex",
                  gap: "5px",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <img
                  src={getScoreAttributes(portfolioScore).smiley}
                  style={{
                    height: "15px",
                    marginRight: "5px",
                    marginTop: "15px",
                    display: portfolioScore > 0 ? "block" : "none",
                  }}
                  alt=""
                />
                <div style={{ display: "flex", flexDirection: "row" }}>
                  <Typography
                    sx={{
                      color: getScoreAttributes(portfolioScore).color,
                      fontFamily: "Rubik, sans-serif",
                      fontWeight: "500",
                      fontSize: "46px",
                      alignContent: "right",
                    }}
                  >
                    {portfolioScore}
                  </Typography>
                </div>
              </div>
            </div>
            <div>
              <BorderLinearProgress
                variant="determinate"
                value={portfolioScore * 10}
                style={{ margin: "6px 22px 10px 22px" }}
              />
            </div>
            <div style={{ height: "50px" }}>
              <hr style={{ marginLeft: "19px", marginRight: "19px" }} />
              <div
                style={{
                  padding: "0 5px 0 18px",
                  display: "flex",
                  flexDirection: "row",
                  alignContent: "center",
                  height: "100%",
                }}
              >
                <img
                  src="/bulbicon.svg"
                  alt="light bulb"
                  style={{ width: "18px", marginRight: "10px" }}
                />
                <Typography
                  sx={{
                    paddingTop: "5px",
                    fontSize: "14px",
                    lineHeight: "15px",
                    zIndex: 2,
                    margin: "auto 0px",
                  }}
                >
                  <button type="button" style={{ color: "#5186EC", background: "none", border: "none", padding: 0, textDecoration: "underline", wordWrap: "break-word", cursor: "pointer" }} onClick={handleBottomClick}>
                    <span style={{ textDecoration: "underline" }}>
                      What can you do to improve it
                    </span>
                  </button>
                </Typography>
              </div>
            </div>
          </Card>
        )}
        {show_existing_customer_card && isAREnabled && !hasExactReceivables && (
          <Card
            variant="outlined"
            style={{
              height: "263px",
              display: "flex",
              flex: 1.5,
              flexDirection: "column",
              justifyContent: "space-between",
              boxShadow: "inset 0px 1px 3px #0000001A, 0px 3px 6px #00000029",
              position: "relative",
              backgroundColor: "#FAFAFC",
            }}
          >
            <div style={bgStyle}></div>
            <div
              style={{
                padding: "5px 20px 0 20px",
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
                height: "50px",
              }}
            >
              <Typography
                style={{
                  color: "#2F3D63",
                  fontFamily: "Rubik, sans-serif",
                  fontSize: "20px",
                  fontWeight: "500",
                  alignContent: "left",
                  marginTop: "10px",
                }}
              >
                Existing Customers
              </Typography>
              <Typography
                style={{
                  color: "#2f3d63",
                  fontFamily: "Rubik, sans-serif",
                  fontSize: "46px",
                  alignContent: "right",
                  fontWeight: "500",
                }}
              >
                {existingCustomers}
              </Typography>
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                paddingLeft: "10px",
                width: "80%",
                justifyContent: "space-between",
                alignSelf: "flex-start",
                marginLeft: "5px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                }}
              >
                <Typography
                  style={{
                    padding: "6px",
                    color: "#2F3D63",
                    fontFamily: "Rubik, sans-serif",
                    fontSize: "14px",
                    alignContent: "left",
                  }}
                >
                  Customers at risk
                </Typography>
                <Typography
                  style={{
                    padding: "6px",
                    color: "#2F3D63",
                    fontFamily: "Rubik, sans-serif",
                    fontSize: "14px",
                  }}
                >
                  Money Exposed to Risk
                </Typography>
              </div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    style={{
                      color: "#F25A48",
                      fontFamily: "Rubik, sans-serif",
                      fontSize: "20px",
                      fontWeight: "500",
                    }}
                  >
                    {customersAtRisk}
                  </Typography>
                  <img
                    src={"/redSmiley.svg"}
                    alt="redSmiley"
                    style={{
                      paddingLeft: "5px",
                      height: "15px",
                      width: "15px",
                    }}
                  />
                </div>
                <Typography
                  style={{
                    color: "#2F3D63",
                    fontFamily: "Rubik, sans-serif",
                    fontSize: "22px",
                    fontWeight: "500",
                  }}
                >
                  ${formatNumber(moneyExposedToRisk)}
                </Typography>
              </div>
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-around",
                marginLeft: "auto",
                marginRight: "20px",
                zIndex: "3",
              }}
            >
              <Box>
                <Typography
                  sx={{
                    color: "#2F3D63",
                    fontWeight: "400",
                    fontSize: "12px",
                    display: agingUpdatedUpTo ? "block" : "none",
                  }}>
                  Last update:
                </Typography>
                <Typography sx={{ color: "#2F3D63", fontWeight: "500", fontSize: "12px" }}>
                  {agingUpdatedUpTo
                    ? new Intl.DateTimeFormat('en-US', { month: 'short', day: '2-digit', year: 'numeric' }).format(new Date(agingUpdatedUpTo))
                    : ""}
                </Typography>
              </Box>
            </div>
            <div style={{ height: "50px" }}>
              <hr style={{ marginLeft: "22px", marginRight: "19px" }} />
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  margin: "0 3px 5px 10px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    whiteSpace: "nowrap",
                    paddingLeft: "10px",
                    marginTop: "10px",
                  }}
                >
                  <img
                    src="/bulbicon.svg"
                    alt="light bulb"
                    style={{ width: "18px", marginRight: "10px" }}
                  />
                  <Typography
                    style={{ fontSize: "14px", paddingTop: "5px", zIndex: "2" }}
                  >
                    <button type="button" style={{ color: "#5186EC", background: "none", border: "none", padding: 0, textDecoration: "underline", wordWrap: "break-word", cursor: "pointer" }} onClick={handleLoadAgingFiles}>
                      Load aging files
                    </button>
                  </Typography>
                </div>
                <div
                  style={{
                    marginLeft: "auto",
                    marginRight: "15px",
                    marginTop: "8px",
                    marginBottom: "auto",
                  }}
                >
                  <DropdownMenu />
                </div>
              </div>
            </div>
          </Card>
        )}
        {show_prospects_card && (
          <Card
            variant="outlined"
            style={{
              height: "263px",
              display: "flex",
              flex: 1,
              flexDirection: "column",
              justifyContent: "space-between",
              boxShadow: "inset 0px 1px 3px #0000001A, 0px 3px 6px #00000029",
              position: "relative",
              backgroundColor: "#FAFAFC",
            }}
          >
            <div style={bgStyle}></div>
            <div
              style={{
                padding: "5px 20px 0 20px",
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
                flex: 1,
              }}
            >
              <Typography
                style={{
                  color: "#2F3D63",
                  fontFamily: "Rubik, sans-serif",
                  marginTop: "10px",
                  fontSize: "20px",
                  alignContent: "left",
                  fontWeight: "500",
                }}
              >
                Prospects
              </Typography>
              <div style={{ display: "flex", flexDirection: "row" }}>
                <Typography
                  style={{
                    color: "#2f3d63",
                    fontFamily: "Rubik, sans-serif",
                    fontSize: "46px",
                    alignContent: "right",
                    fontWeight: "500",
                  }}
                >
                  {prospectsRows.length}
                </Typography>
              </div>
            </div>
            <div style={{ flex: 1.5, marginLeft: "1px" }}>
              <Typography
                style={{
                  color: "#2f3d63",
                  paddingLeft: "20px",
                  fontFamily: "Rubik, sans-serif",
                  fontSize: "15px",
                }}
              >
                Sum of approved limit
              </Typography>
              <Typography
                style={{
                  color: "#2f3d63",
                  paddingLeft: "20px",
                  fontWeight: "500",
                  fontFamily: "Rubik, sans-serif",
                  fontSize: "24px",
                }}
              >
                ${formatNumber(totalAmount)}
              </Typography>
            </div>
            <div style={{ height: "50px" }}>
              <hr style={{ marginLeft: "19px", marginRight: "19px" }} />
              <div
                style={{
                  padding: "10px",
                  display: "flex",
                  flexDirection: "row",
                  alignContent: "center",
                  marginLeft: "6px",
                }}
              >
                <img
                  src="/bulbicon.svg"
                  alt="light bulb"
                  style={{ width: "18px", marginRight: "10px" }}
                />
                <Typography
                  style={{ paddingTop: "5px", fontSize: "14px", zIndex: "2" }}
                >
                  <button type="button" style={{ color: "#5186EC", background: "none", border: "none", padding: 0, textDecoration: "underline", wordWrap: "break-word", cursor: "pointer" }} onClick={() => {}}>
                    <span>
                      Add company to monitor
                    </span>
                  </button>
                </Typography>
              </div>
            </div>
          </Card>
        )}
      </div>
      {(tenantId === "2ab5560a-591e-49c4-b21c-728422144237" || tenantId === "80ca91bd-bc50-40c0-90b7-f0e65294d10e") && (
        <div style={{...containerStyle, gap: '16px', marginTop: '20px'}}>
            <Card variant="outlined" style={{flex: 1, height: '150px', padding: '20px', cursor: 'pointer', position: 'relative', backgroundColor: "#FAFAFC", boxShadow: "inset 0px 1px 3px #0000001A, 0px 3px 6px #00000029"}} onClick={() => handleStatusClick('PENDING')}>
                <div style={bgStyle}></div>
                <Typography sx={{color: "#2F3D63", fontFamily: "Rubik, sans-serif", fontSize: "20px", fontWeight: "500"}}>Pending</Typography>
                <Typography sx={{color: "#2f3d63", fontFamily: "Rubik, sans-serif", fontSize: "46px", fontWeight: "500"}}>{pendingCount}</Typography>
            </Card>
            <Card variant="outlined" style={{flex: 1, height: '150px', padding: '20px', cursor: 'pointer', position: 'relative', backgroundColor: "#FAFAFC", boxShadow: "inset 0px 1px 3px #0000001A, 0px 3px 6px #00000029"}} onClick={() => handleStatusClick('APPROVED')}>
                <div style={bgStyle}></div>
                <Typography sx={{color: "#2F3D63", fontFamily: "Rubik, sans-serif", fontSize: "20px", fontWeight: "500"}}>Approved</Typography>
                <Typography sx={{color: "#2f3d63", fontFamily: "Rubik, sans-serif", fontSize: "46px", fontWeight: "500"}}>{approvedCount}</Typography>
            </Card>
        </div>
      )}
      </>
      )}
      {!isProspects && (tenantId || URLTenant) && <>
        <ReceivablesWorkspace tenantId={tenantId || URLTenant} rows={rows} onCommitted={loadSummaryData} />
        <AgingImportLog tenantId={tenantId || URLTenant} />
      </>}
      {show_customer_risk_bar_chart && !hasExactReceivables && (
        <RiskDistributionDiagram
          rowData={isProspects ? prospectsRows : rows}
          adminAction={adminAction}
        />
      )}
      {isProspects || !show_portfolio_score_chart || hasExactReceivables ? null : (
        <PortfolioScoreChart
          currentScore={portfolioScore}
          currentCompanyCount={processedData.filter((item) => {
            const score = Number(item["Combined Score"]);
            return Number.isFinite(score) && score > 0;
          }).length}
        />
      )}
      {show_ar_heatmap && !isProspects && !hasExactReceivables && (
        <ARHeatmapNew 
          summary={rows} 
          adminAction={isAdmin} 
          tableType={"total"} 
        />
      )}
      <IntegratedPolicyAction key={`${requesterId}:${selectedTenantId}:${identityReady}`} tenantId={tenantId} insuranceEnabled={isInsuranceEnabled && directInsuranceAccess} onPolicyChange={integratedDcl.reload} />
      {show_summary_table && (
        <RecommendationTable
          rows={isProspects ? prospectsRows : rows}
          showAtRisk={showAtRisk}
          setShowAtRisk={setShowAtRisk}
          riskyRows={isProspects ? sortProspectRiskyRows(prospectsRows) : sortARriskyRows(rows)}
          hasApprovalRules={hasApprovalRules}
          showInsured={isInsuranceEnabled}
          requesterId={requesterId}
          brokerLimitComparisons={brokerLimitComparisons}
          insuredLimitMap={insuredLimitMap}
          integratedDcl={integratedDcl}
        />
      )}
      <Box ref={bottomRef} id="bottom" sx={{ height: "1px" }} />
      <Modal
        open={popupOpen}
        onClose={handleClosePopup}
        aria-labelledby="ar-disabled-popup-title"
        aria-describedby="ar-disabled-popup-description"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            width: "400px",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            borderRadius: "20px",
            boxShadow: "inset 0px 1px 3px #0000001A, 0px 3px 6px #00000029",
            padding: "20px",
            paddingTop: "10px",
            paddingLeft: "45px",
          }}
        >
          <Typography
            id="ar-disabled-popup-title"
            variant="h6"
            component="h2"
            sx={{ color: "#2F3D63" }}
          >
            Accounts Receivable Not Enabled
          </Typography>
          <Typography
            id="ar-disabled-popup-description"
            sx={{ mt: 2, color: "#3d4e7a" }}
          >
            You currently don't have Accounts Receivable enabled. To enable it,
            please click on the link:
            <span style={{ marginLeft: "10px" }}>
              <button type="button" style={{ color: "#5186EC", background: "none", border: "none", padding: 0, textDecoration: "underline", wordWrap: "break-word", cursor: "pointer" }} onClick={(e) => {
                e.preventDefault();
                handleSettingsClick();
              }}>
                go to settings
              </button>
            </span>
          </Typography>

          <IconButton
            sx={{ position: "absolute", top: 5, left: 5 }}
            onClick={handleClosePopup}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </Modal>
      </>
      )}
    </>
  )
}
